"use client";

import { useSyncExternalStore } from "react";
import { dayLabel, localClock, weekdayShort } from "./area-format";

/**
 * A forecast day's name. The server HTML always carries the weekday or date, because an ISR page
 * rendered late in the evening can still be served after local midnight; after hydration the day
 * that matches the viewer's clock in the area's timezone becomes "Today" (spec sections 10.1 and
 * 15 risk 5).
 */

const MINUTE_MS = 60_000;

/** Re-reads the clock once a minute. */
export function subscribeToMinute(callback: () => void): () => void {
  const id = window.setInterval(callback, MINUTE_MS);
  return () => window.clearInterval(id);
}

/** The viewer's local date in the timezone, or null on the server and before hydration. */
export function useAreaToday(timezone: string): string | null {
  return useSyncExternalStore(subscribeToMinute, () => localClock(Date.now(), timezone).dateISO, () => null);
}

export function AreaDayName({
  dateISO,
  timezone,
  format = "weekday",
}: Readonly<{ dateISO: string; timezone: string; format?: "weekday" | "date" }>) {
  const today = useAreaToday(timezone);
  if (today === dateISO) return <>Today</>;
  return <>{format === "date" ? dayLabel(dateISO) : weekdayShort(dateISO)}</>;
}

/**
 * The heatmap's "Today" marks, after hydration only: a small "Today" label over the column whose
 * date is the viewer's date in the area's timezone, and the ring around that column's cells, by a
 * scoped style rule. Before hydration (and on the server) no column claims to be today.
 */
export function AreaTodayLabel({ dateISO, timezone, className }: Readonly<{ dateISO: string; timezone: string; className?: string }>) {
  const today = useAreaToday(timezone);
  return today === dateISO ? <span className={className}>Today</span> : null;
}

export function AreaTodayStyle({ scope, timezone, css }: Readonly<{
  /** The `data-heat-scope` value of the heatmap. */
  scope: string;
  timezone: string;
  /** The declarations for today's cells, for example the ring's box-shadow. */
  css: string;
}>) {
  const today = useAreaToday(timezone);
  if (!today) return null;
  const at = `[data-heat-scope="${scope.replace(/["\\]/gu, "")}"]`;
  return <style>{`${at} [data-heat-date="${today}"]{${css}}${at} [data-heat-day="${today}"]{color:#15727F}`}</style>;
}
